import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import EpisodeFollowUpsSection from "../components/intake/EpisodeFollowUpsSection";
import RecordDetailsForm from "../components/intake/RecordDetailsForm";
import Form from "../components/form/Form";
import PageSectionCard from "../components/PageSectionCard";
import { useAppSession } from "../context/AppSessionContext";
import {
  CreateEpisodeFormValues,
  reasonCodeOptions,
  toIsoFromLocalDateTime,
} from "../lib/intakeForm";
import { readErrorMessage } from "../lib/http";
import { getEmployeeClientCompany } from "../types/employees";
import { EpisodeComment, EpisodeDetailsResponse } from "../types/incapacity";

const emptyFormValues: CreateEpisodeFormValues = {
  reportedAt: "",
  startDate: "",
  estimatedReturnDate: "",
  returnDate: "",
  reasonCode: "",
  verified: false,
  musculoskeletal: false,
  comment: "",
  followUps: [],
};

const formatDateTime = (value: string | null) => {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

const toDateInput = (value: string | null) => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toISOString().slice(0, 10);
};

const toDateTimeLocalInput = (value: string | null) => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const localDate = new Date(
    parsed.getTime() - parsed.getTimezoneOffset() * 60_000,
  );
  return localDate.toISOString().slice(0, 16);
};

const isKnownReasonCode = (
  value: string,
): value is CreateEpisodeFormValues["reasonCode"] =>
  reasonCodeOptions.some((reasonCode) => reasonCode === value);

const toEditValues = (
  episode: EpisodeDetailsResponse,
): CreateEpisodeFormValues => ({
  reportedAt: toDateTimeLocalInput(episode.reportedAt),
  startDate: toDateInput(episode.startDate),
  estimatedReturnDate: toDateInput(episode.estimatedReturnDate),
  returnDate: toDateInput(episode.returnDate),
  reasonCode: isKnownReasonCode(episode.reasonCode) ? episode.reasonCode : "",
  verified: episode.verified,
  musculoskeletal: episode.musculoskeletal,
  comment: "",
  followUps: [...(episode.followUpTasks ?? [])]
    .filter((task) => !task.deletedAt)
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
    .map((task) => ({
      id: task.id,
      taskType: task.type,
      dueAt: toDateTimeLocalInput(task.dueDate),
      description: task.description ?? "",
      status: task.status,
    })),
});

function EpisodeDetailsPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { authorizedFetch, canMaintainEmployees, canManageClientCompanies } =
    useAppSession();

  const [episode, setEpisode] = useState<EpisodeDetailsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [followUpErrors, setFollowUpErrors] = useState<string[]>([]);
  const [removedFollowUpIds, setRemovedFollowUpIds] = useState<string[]>([]);
  const [commentText, setCommentText] = useState("");
  const [commentError, setCommentError] = useState<string | null>(null);
  const [isAddingComment, setIsAddingComment] = useState(false);

  const form = useForm<CreateEpisodeFormValues>({
    defaultValues: emptyFormValues,
  });

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      setLoadError(t("Missing episode ID in route."));
      return;
    }

    let cancelled = false;

    const loadEpisode = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await authorizedFetch(`/episodes/${id}`);

        if (!response.ok) {
          throw new Error(
            await readErrorMessage(
              response,
              response.status === 404
                ? t("Episode not found.")
                : t("Failed to load episode ({{status}}).", {
                    status: response.status,
                  }),
            ),
          );
        }

        const payload = (await response.json()) as EpisodeDetailsResponse;

        if (!cancelled) {
          setEpisode(payload);
          setRemovedFollowUpIds([]);
          form.reset(toEditValues(payload));
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        const message =
          error instanceof Error ? error.message : t("Failed to load episode.");
        setLoadError(message);
        setEpisode(null);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadEpisode();

    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, form, id, t]);

  const comments = useMemo(
    () =>
      [...(episode?.comments ?? [])].sort((a, b) =>
        a.createdAt.localeCompare(b.createdAt),
      ),
    [episode?.comments],
  );

  const onSave = async (values: CreateEpisodeFormValues) => {
    if (!id || !canMaintainEmployees) {
      setSaveError(t("You are not allowed to edit this episode."));
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setFollowUpErrors([]);

    try {
      const response = await authorizedFetch(`/episodes/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          reportedAt: toIsoFromLocalDateTime(values.reportedAt),
          startDate: values.startDate,
          estimatedReturnDate: values.estimatedReturnDate,
          // null clears the date; undefined would be dropped and leave it as is.
          returnDate: values.returnDate || null,
          reasonCode: values.reasonCode,
          verified: values.verified,
          musculoskeletal: values.musculoskeletal,
        }),
      });

      if (!response.ok) {
        throw new Error(
          await readErrorMessage(
            response,
            response.status === 403
              ? t("You are not authorized to edit episodes.")
              : t("Failed to update episode ({{status}}).", {
                  status: response.status,
                }),
          ),
        );
      }

      const followUpOps: { label: string; run: () => Promise<void> }[] = [];

      const dirtyFollowUps = form.formState.dirtyFields.followUps;
      const isFollowUpRowDirty = (index: number) => {
        const rowDirty = dirtyFollowUps?.[index];
        if (!rowDirty) {
          return false;
        }
        return Boolean(rowDirty.taskType || rowDirty.dueAt || rowDirty.status);
      };

      values.followUps.forEach((followUp, index) => {
        if (!followUp.taskType || !followUp.dueAt) {
          return;
        }

        const body = {
          type: followUp.taskType,
          dueDate: toIsoFromLocalDateTime(followUp.dueAt),
        };
        const label = t("Follow-up {{index}}", { index: index + 1 });

        if (followUp.id) {
          // Existing follow-up: only update it if the user actually changed it.
          if (!isFollowUpRowDirty(index)) {
            return;
          }

          const followUpId = followUp.id;
          followUpOps.push({
            label,
            run: async () => {
              const res = await authorizedFetch(`/follow-ups/${followUpId}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...body, status: followUp.status }),
              });

              if (!res.ok) {
                throw new Error(
                  await readErrorMessage(
                    res,
                    t("Failed to update follow-up ({{status}}).", {
                      status: res.status,
                    }),
                  ),
                );
              }
            },
          });
        } else {
          followUpOps.push({
            label,
            run: async () => {
              const res = await authorizedFetch("/follow-ups", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ episodeId: id, ...body }),
              });

              if (!res.ok) {
                throw new Error(
                  await readErrorMessage(
                    res,
                    t("Failed to create follow-up ({{status}}).", {
                      status: res.status,
                    }),
                  ),
                );
              }
            },
          });
        }
      });

      removedFollowUpIds.forEach((removedId) => {
        followUpOps.push({
          label: t("Removed follow-up"),
          run: async () => {
            const res = await authorizedFetch(`/follow-ups/${removedId}`, {
              method: "DELETE",
            });

            if (!res.ok && res.status !== 404) {
              throw new Error(
                await readErrorMessage(
                  res,
                  t("Failed to remove follow-up ({{status}}).", {
                    status: res.status,
                  }),
                ),
              );
            }
          },
        });
      });

      const followUpResults = await Promise.allSettled(
        followUpOps.map((op) => op.run()),
      );

      const failures = followUpResults.reduce<string[]>(
        (messages, result, index) => {
          if (result.status === "fulfilled") {
            return messages;
          }

          const reason =
            result.reason instanceof Error
              ? result.reason.message
              : t("Something went wrong.");
          messages.push(
            t("{{label}}: {{reason}}", {
              label: followUpOps[index].label,
              reason,
            }),
          );
          return messages;
        },
        [],
      );

      const refreshResponse = await authorizedFetch(`/episodes/${id}`);

      if (!refreshResponse.ok) {
        throw new Error(
          await readErrorMessage(
            refreshResponse,
            t("Failed to refresh episode ({{status}}).", {
              status: refreshResponse.status,
            }),
          ),
        );
      }

      const refreshed =
        (await refreshResponse.json()) as EpisodeDetailsResponse;
      setEpisode(refreshed);
      setRemovedFollowUpIds([]);
      form.reset(toEditValues(refreshed));

      if (failures.length > 0) {
        setSaveError(t("Episode updated, but one or more follow-ups failed."));
        setFollowUpErrors(failures);
        return;
      }

      toast.success(t("Episode updated successfully."));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t("Failed to update episode.");
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const onAddComment = async () => {
    if (!id || !episode || !canMaintainEmployees) {
      setCommentError(
        t("You are not allowed to add comments to this episode."),
      );
      return;
    }

    const text = commentText.trim();

    if (!text) {
      setCommentError(t("Comment text is required."));
      return;
    }

    setIsAddingComment(true);
    setCommentError(null);

    try {
      const response = await authorizedFetch(`/episodes/${id}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          text,
          employeeId: episode.employeeId,
        }),
      });

      if (!response.ok) {
        throw new Error(
          await readErrorMessage(
            response,
            response.status === 403
              ? t("You are not authorized to add comments.")
              : t("Failed to add comment ({{status}}).", {
                  status: response.status,
                }),
          ),
        );
      }

      const createdComment = (await response.json()) as EpisodeComment;

      setEpisode((current) =>
        current
          ? {
              ...current,
              comments: [...(current.comments ?? []), createdComment],
            }
          : current,
      );
      setCommentText("");
      toast.success(t("Comment added successfully."));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t("Failed to add comment.");
      setCommentError(message);
    } finally {
      setIsAddingComment(false);
    }
  };

  const formId = "episode-edit-form";
  const canSave = !!episode && !isLoading && !loadError && canMaintainEmployees;
  const isSavingEpisode = isSaving || form.formState.isSubmitting;

  return (
    <PageSectionCard
      titleId="episode-details-title"
      eyebrow={t("Episode")}
      title={t("Episode details")}
      lead={t(
        "Review and edit episode details, add follow-up tasks, and monitor existing tasks.",
      )}
      stickyHeader
      actions={
        <div className="headerActions">
          <Link className="secondaryButton" to="/intake">
            {t("Create episode")}
          </Link>
          <button
            className="primaryButton"
            type="submit"
            form={formId}
            disabled={!canSave || isSavingEpisode}
          >
            {isSavingEpisode ? t("Saving...") : t("Save changes")}
          </button>
        </div>
      }
    >
      {isLoading ? <p>{t("Loading episode details...")}</p> : null}

      {loadError ? (
        <p className="errorMessage" role="alert">
          {loadError}
        </p>
      ) : null}

      {episode && !isLoading && !loadError ? (
        <>
          <section className="subCard" aria-labelledby="employee-heading">
            <h3 id="employee-heading" className="subCardTitle">
              {t("Employee")}
            </h3>

            <dl className="summaryGrid">
              <div>
                <dt className="summaryLabel">{t("Name")}</dt>
                <dd className="summaryValue">{episode.employee.fullName}</dd>
              </div>

              <div>
                <dt className="summaryLabel">{t("Client company")}</dt>
                <dd className="summaryValue">
                  {getEmployeeClientCompany(episode.employee)?.name ?? "-"}
                </dd>
              </div>

              <div>
                <dt className="summaryLabel">{t("Workstation")}</dt>
                <dd className="summaryValue">
                  {episode.employee.workstation?.name ?? "-"}
                </dd>
              </div>

              <div>
                <dt className="summaryLabel">{t("Phone")}</dt>
                <dd className="summaryValue">
                  {episode.employee.phone ?? "-"}
                </dd>
              </div>

              <div>
                <dt className="summaryLabel">{t("Landline")}</dt>
                <dd className="summaryValue">
                  {episode.employee.landline || "-"}
                </dd>
              </div>

              <div>
                <dt className="summaryLabel">{t("Employee number")}</dt>
                <dd className="summaryValue">
                  {episode.employee.nationalId || "-"}
                </dd>
              </div>
            </dl>
          </section>

          <Form
            id={formId}
            className="intakeFormLayout"
            form={form}
            onSubmit={onSave}
          >
            <RecordDetailsForm
              form={form}
              stepLabel={t("Episode details")}
              showVerifiedField
            />

            <section
              className="subCard"
              aria-labelledby="episode-comments-heading"
            >
              <h3 id="episode-comments-heading" className="subCardTitle">
                {t("Comments")}
              </h3>

              {comments.length === 0 ? (
                <p className="hintText sectionHint">
                  {t("No comments have been added yet.")}
                </p>
              ) : (
                <ol className="commentList" aria-label={t("Episode comments")}>
                  {comments.map((comment) => (
                    <li className="commentItem" key={comment.id}>
                      <div className="commentMeta">
                        <span className="commentAuthor">
                          {comment.author?.fullName ||
                            comment.author?.email ||
                            t("Unknown author")}
                        </span>
                        <time
                          className="commentTime"
                          dateTime={comment.createdAt}
                        >
                          {formatDateTime(comment.createdAt)}
                        </time>
                      </div>
                      <p className="commentText">{comment.text}</p>
                    </li>
                  ))}
                </ol>
              )}

              <div className="commentForm">
                <label className="field" htmlFor="episode-comment-text">
                  <span className="fieldLabel">{t("New comment")}</span>
                  <textarea
                    id="episode-comment-text"
                    className="inputControl textareaControl"
                    value={commentText}
                    onChange={(event) => setCommentText(event.target.value)}
                    disabled={!canMaintainEmployees || isAddingComment}
                  />
                </label>

                <button
                  className="primaryButton"
                  type="button"
                  onClick={onAddComment}
                  disabled={!canMaintainEmployees || isAddingComment}
                >
                  {isAddingComment ? t("Adding...") : t("Add comment")}
                </button>

                {!canMaintainEmployees ? (
                  <p className="hintText sectionHint">
                    {t("You are not allowed to add comments to this episode.")}
                  </p>
                ) : null}

                {commentError ? (
                  <p className="errorMessage" role="alert">
                    {commentError}
                  </p>
                ) : null}
              </div>
            </section>

            <EpisodeFollowUpsSection
              form={form}
              stepLabel={t("Follow-ups")}
              canDelete={canManageClientCompanies}
              onRemoveExisting={(followUpId) =>
                setRemovedFollowUpIds((current) =>
                  current.includes(followUpId)
                    ? current
                    : [...current, followUpId],
                )
              }
            />

            {followUpErrors.length > 0 ? (
              <ul className="followUpErrorList" role="alert">
                {followUpErrors.map((errorMessage) => (
                  <li key={errorMessage}>{errorMessage}</li>
                ))}
              </ul>
            ) : null}

            {!canMaintainEmployees ? (
              <p className="hintText sectionHint">
                {t("You are not allowed to edit this episode.")}
              </p>
            ) : null}

            {saveError ? (
              <p className="errorMessage" role="alert">
                {saveError}
              </p>
            ) : null}
          </Form>
        </>
      ) : null}
    </PageSectionCard>
  );
}

export default EpisodeDetailsPage;
